import React, { useMemo, useRef, useState } from 'react';
import ReactImageGallery from 'react-image-gallery';
import { useSelector } from 'react-redux';
import { useLoaderData } from 'react-router-dom';

import Descriptor from '@r6digital/rapidstor-types/react/entities/Descriptor';
import Location from '@r6digital/rapidstor-types/react/entities/Location';
import UnitType, {
    distributeUnitsToGroups,
    UnitGroup,
} from '@r6digital/rapidstor-types/react/entities/UnitType';

import { selectUnitData } from '../../store/bookingSlice';

import classes from './UnitType.module.scss';

import Spinner from '../Spinner';

interface Props extends React.HTMLAttributes<HTMLElement> {
    descriptor: Descriptor;
    unitType: UnitType;
    recommended: boolean;
    reservationIsLoading: boolean;
    onBookUnitClicked: () => void;
    onEmailQuoteClicked: () => void;
}

function UnitTypeElement({
    onBookUnitClicked,
    onEmailQuoteClicked,
    descriptor,
    unitType,
    reservationIsLoading,
}: Props) {
    const unitGroups = distributeUnitsToGroups(descriptor.unitTypes);
    const location = useLoaderData() as Location;

    const groupLabels: Record<UnitGroup, string> = {
        upper: 'Upper',
        ground: 'Ground',
        driveup: 'Drive Up',
    };

    const CALL_FOR_AVAILABILITY_THRESHOLD = location.rapidstor.minAvailableUnits;

    const [loading, setLoading] = useState<boolean>(false);
    const unitData = useSelector(selectUnitData);

    const totalDescriptorAvailability: number = useMemo(() => {
        return descriptor.unitTypes.reduce(
            (total, unitType) => total + (unitType.totalAvailable || 0),
            0
        );
    }, [descriptor.unitTypes]);

    const totalDescriptorUnitTypesUnitLength = useMemo(() => {
        return descriptor.unitTypes.reduce(
            (total, unitType) => total + (unitType.units?.length || 0),
            0
        );
    }, [descriptor.unitTypes]);

    // Remove groups that don't have a unit type assigned to them.
    // Slight duplication from the Descriptor component.
    // @TODO: extract method to UnitType.ts.
    const validGroups: string[] = Object.entries(unitGroups)
        .filter(([, type]) => type)
        .map(([group]) => groupLabels[group as UnitGroup]);
    validGroups.sort();

    let levels: string[] = descriptor.unitTypes.map((type) => '' + type.floor);
    levels.sort();
    levels = [...new Set(levels.map((floor) => (floor === '0' ? 'G' : floor)))];

    // const [showModal, setShowModal] = useState<boolean>(false);
    // const modalOnHide = () => setShowModal(false);

    const reactImageGallery = useRef<ReactImageGallery>(null);
    const [showImageGallery, setShowImageGallery] = useState<boolean>(false);
    const images = descriptor.slides.map((value) => {
        return {
            original: value,
            thumbnail: value,
        };
    });

    const rateToDisplay = useMemo(() => {
        if (unitType.rates.discounted) {
            return unitType.rates.discountedRate || unitType.rates.rate;
        }
        return unitType.rates.rate;
    }, [unitType]);

    return (
        <>
            {/* <span className={`${classes.badge}`}>Label?</span> */}
            <span className={`${classes.unitTypeName}`}>{descriptor.name}</span>
            <span className={`${classes.dimensions}`}>{descriptor.description}</span>
            <div
                className={`${classes.imagePanel}`}
                // onClick={() => setShowModal(true)}
            >
                <img
                    src={descriptor.picture}
                    className={`${classes.unitImage} mw-100`}
                    aria-hidden
                    alt={'Computer render of a storage unit'}
                    onClick={() => {
                        if (images.length > 0) {
                            setShowImageGallery(true);
                            reactImageGallery.current?.fullScreen();
                        }
                    }}
                />
                <div
                    style={{
                        display: showImageGallery ? 'block' : 'none',
                    }}
                >
                    <ReactImageGallery
                        ref={reactImageGallery}
                        items={images}
                        showPlayButton={false}
                        onScreenChange={(fullscreen) => {
                            if (!fullscreen) {
                                setShowImageGallery(false);
                            }
                        }}
                    />
                </div>
            </div>
            <div className={`${classes.unitDetails}`}>
                <div className={`${classes.price}`}>
                    <div>
                        <span>From ${rateToDisplay}</span>
                        <span>/month</span>
                    </div>

                    {unitType.rates.discounted && unitType.rates.discountedRate !== 0 && (
                        <span className={`${classes.original_price}`}>
                            ${unitType.rates.rate} /month
                        </span>
                    )}

                    {images.length > 0 && (
                        <svg
                            width='17'
                            height='17'
                            viewBox='0 0 17 17'
                            fill='none'
                            xmlns='http://www.w3.org/2000/svg'
                            onClick={() => {
                                if (images.length > 0) {
                                    setShowImageGallery(true);
                                    reactImageGallery.current?.fullScreen();
                                }
                            }}
                        >
                            <path
                                d='M8.5 17C7.71493 17 6.96233 16.8997 6.24219 16.699C5.52205 16.4983 4.84323 16.2149 4.20573 15.849C3.56823 15.483 2.99566 15.0403 2.48802 14.5208C1.98038 14.0014 1.53767 13.4259 1.1599 12.7943C0.782118 12.1627 0.495833 11.4868 0.301042 10.7667C0.10625 10.0465 0.00590278 9.29097 0 8.5C0 7.71493 0.100347 6.96233 0.301042 6.24219C0.501736 5.52205 0.785069 4.84323 1.15104 4.20573C1.51701 3.56823 1.95972 2.99566 2.47917 2.48802C2.99861 1.98038 3.57413 1.53767 4.20573 1.1599C4.83733 0.782118 5.51319 0.495833 6.23333 0.301042C6.95347 0.10625 7.70903 0.00590278 8.5 0C9.28507 0 10.0377 0.100347 10.7578 0.301042C11.478 0.501736 12.1568 0.785069 12.7943 1.15104C13.4318 1.51701 14.0043 1.95972 14.512 2.47917C15.0196 2.99861 15.4623 3.57413 15.8401 4.20573C16.2179 4.83733 16.5042 5.51319 16.699 6.23333C16.8937 6.95347 16.9941 7.70903 17 8.5C17 9.28507 16.8997 10.0377 16.699 10.7578C16.4983 11.478 16.2149 12.1568 15.849 12.7943C15.483 13.4318 15.0403 14.0043 14.5208 14.512C14.0014 15.0196 13.4259 15.4623 12.7943 15.8401C12.1627 16.2179 11.4868 16.5042 10.7667 16.699C10.0465 16.8937 9.29097 16.9941 8.5 17ZM8.5 1.13333C7.82118 1.13333 7.16892 1.22187 6.54323 1.39896C5.91753 1.57604 5.33316 1.82396 4.7901 2.14271C4.24705 2.46146 3.74826 2.84514 3.29375 3.29375C2.83924 3.74236 2.45556 4.23819 2.14271 4.78125C1.82986 5.32431 1.58194 5.91163 1.39896 6.54323C1.21597 7.17483 1.12743 7.82708 1.13333 8.5C1.13333 9.17292 1.22187 9.82222 1.39896 10.4479C1.57604 11.0736 1.82396 11.6609 2.14271 12.2099C2.46146 12.7589 2.84514 13.2576 3.29375 13.7063C3.74236 14.1549 4.23819 14.5385 4.78125 14.8573C5.32431 15.176 5.91163 15.424 6.54323 15.601C7.17483 15.7781 7.82708 15.8667 8.5 15.8667C9.17292 15.8667 9.82222 15.7781 10.4479 15.601C11.0736 15.424 11.6609 15.176 12.2099 14.8573C12.7589 14.5385 13.2576 14.1549 13.7063 13.7063C14.1549 13.2576 14.5385 12.7618 14.8573 12.2188C15.176 11.6757 15.424 11.0884 15.601 10.4568C15.7781 9.82517 15.8667 9.17292 15.8667 8.5C15.8667 7.82708 15.7781 7.17778 15.601 6.55208C15.424 5.92639 15.176 5.33906 14.8573 4.7901C14.5385 4.24115 14.1549 3.74236 13.7063 3.29375C13.2576 2.84514 12.7618 2.46146 12.2188 2.14271C11.6757 1.82396 11.0884 1.57604 10.4568 1.39896C9.82517 1.22187 9.17292 1.13333 8.5 1.13333ZM7.93333 6.8H9.06667V12.4667H7.93333V6.8ZM7.93333 4.53333H9.06667V5.66667H7.93333V4.53333Z'
                                fill='#ACACAC'
                            />
                        </svg>
                    )}
                </div>
                <span className={`${classes.description}`}>
                    {descriptor.descriptions && descriptor.descriptions[0]}
                </span>
                <div className={`${classes.buttons}`}>
                    {/* <button
                        disabled={loading || reservationIsLoading}
                        onClick={onEmailQuoteClicked}
                        className={`${
                            totalDescriptorAvailability > 0
                                ? classes.email_quote
                                : classes.email_quote_disabled
                        }`}
                    >
                        Email Quote
                    </button> */}
                    {(totalDescriptorAvailability <= CALL_FOR_AVAILABILITY_THRESHOLD ||
                        totalDescriptorUnitTypesUnitLength === 0) && (
                        <button
                            disabled={loading || reservationIsLoading}
                            onClick={() => {
                                window.open(`tel:${location.contactPhone}`);
                            }}
                            className={`${classes.book_now} ${classes.callForQuote}`}
                        >
                            <span className={`${classes.contactPhone}`}>
                                Call For Availability
                            </span>
                            <span className={`${classes.callForQuoteText}`}>
                                {location.contactPhone.slice(2)}
                            </span>
                        </button>
                    )}
                    {totalDescriptorAvailability > CALL_FOR_AVAILABILITY_THRESHOLD &&
                        totalDescriptorUnitTypesUnitLength > 0 && (
                            <button
                                disabled={loading || reservationIsLoading}
                                onClick={() => {
                                    onBookUnitClicked();
                                    setLoading(true);
                                }}
                                className={`${classes.book_now}`}
                                data-unit-size-selected={descriptor.name}
                            >
                                {reservationIsLoading &&
                                unitData?.unitType._id === unitType._id ? (
                                    <Spinner inverse size={25} />
                                ) : (
                                    `Book a Unit`
                                )}
                            </button>
                        )}
                </div>
                {!reservationIsLoading &&
                    totalDescriptorAvailability > CALL_FOR_AVAILABILITY_THRESHOLD && (
                        <div
                            className={`${classes.final_section}`}
                            onClick={onEmailQuoteClicked}
                            aria-disabled={loading || reservationIsLoading}
                        >
                            <svg
                                width='17'
                                height='18'
                                viewBox='0 0 17 18'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                            >
                                <path
                                    d='M16.28 9C16.28 4.78024 12.8597 1.36 8.64 1.36C4.42024 1.36 1 4.78024 1 9C1 13.2197 4.42024 16.64 8.64 16.64C12.8597 16.64 16.28 13.2197 16.28 9Z'
                                    stroke='#919191'
                                    strokeWidth='1.44'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                />
                                <path
                                    d='M10.3864 7.62795V7.56514C10.3864 6.96387 9.89905 6.47655 9.29778 6.47655H8.20916C7.4832 6.47655 6.89429 7.06463 6.89429 7.79145C6.89429 8.5175 7.4832 9.10638 8.20916 9.10638H9.12265C9.82061 9.10638 10.3872 9.67216 10.3872 10.37C10.3872 11.0688 9.82061 11.6346 9.12265 11.6346H7.98285C7.38158 11.6346 6.89429 11.1464 6.89429 10.5452'
                                    stroke='#919191'
                                    strokeWidth='1.44'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                />
                                <path
                                    d='M8.64355 12.441V11.639M8.64355 6.47403V5.56466'
                                    stroke='#919191'
                                    strokeWidth='1.44'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                />
                            </svg>
                            <span>Secure Pricing</span>
                        </div>
                    )}
            </div>
        </>
    );
}

export default UnitTypeElement;
